import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useRef,
  useEffect,
} from "react";
import { CustomCellEditorProps } from "ag-grid-react";
import {
  Popover,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { Button, TextField, Typography } from "@platform/design-system";

export interface PopoverSelectEditorParams {
  options?: string[];
  title?: string;
  placeholder?: string;
  otherLabel?: string;
}

export interface PopoverSelectEditorRef {
  getValue: () => string;
}

const DEFAULT_OPTIONS = ["Option One", "Option Two", "Other"];

/**
 * Popover-based select editor with radio buttons and custom "Other" input
 * Used for single-selection fields like Network Name
 */
export const PopoverSelectEditor = forwardRef<
  PopoverSelectEditorRef,
  CustomCellEditorProps<Record<string, unknown>, string, unknown>
>((props, ref) => {
  const { stopEditing, api, node, column } = props;
  const initialValue = props.value || "";

  // Get params from cellEditorParams
  const editorParams =
    (props as unknown as { cellEditorParams?: unknown }).cellEditorParams ??
    props;
  const params = editorParams as PopoverSelectEditorParams;
  const options = params?.options || DEFAULT_OPTIONS;
  const title = params?.title || "Select Option";
  const placeholder = params?.placeholder || "Enter custom value";
  const otherLabel = params?.otherLabel || "Other";

  // Determine if initial value is a predefined option or custom
  const isPredefinedOption = options.includes(initialValue);
  const [selectedOption, setSelectedOption] = useState<string>(
    isPredefinedOption ? initialValue : otherLabel,
  );
  const [customValue, setCustomValue] = useState<string>(
    isPredefinedOption ? "" : initialValue,
  );

  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  // Expose getValue method to AG Grid
  useImperativeHandle(ref, () => ({
    getValue: () => {
      if (selectedOption === otherLabel) {
        return customValue;
      }
      return selectedOption;
    },
  }));

  // Auto-open popover when editor is created
  useEffect(() => {
    const timer = setTimeout(() => {
      setOpen(true);
    }, 10);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = (): void => {
    setOpen(false);
    if (stopEditing) {
      setTimeout(() => {
        stopEditing();
      }, 0);
    }
  };

  const handleDone = (): void => {
    // Get the final value
    const finalValue =
      selectedOption === otherLabel ? customValue : selectedOption;

    // Set the data value directly on the node
    if (node && column) {
      const columnField = column.getColId();
      node.setDataValue(columnField, finalValue);
    }

    // Stop editing and refresh
    if (stopEditing) {
      stopEditing();
    }

    if (api && node) {
      setTimeout(() => {
        api.refreshCells({ rowNodes: [node], force: true });
      }, 0);
    }

    setOpen(false);
  };

  const handleOptionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setSelectedOption(event.target.value);
  };

  const isOtherSelected = selectedOption === otherLabel;

  return (
    <>
      <div ref={anchorRef} style={{ width: "100%", height: "100%" }} />
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        disableEnforceFocus
        disableAutoFocus={false}
        disableRestoreFocus
        slotProps={{
          paper: {
            sx: {
              minWidth: 280,
              maxWidth: 320,
              p: 2,
            },
          },
        }}
      >
        <Box
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              event.stopPropagation();
              handleDone();
            }
          }}
        >
          <Typography
            variant="subtitle2"
            color="textSecondary"
            sx={{ mb: 1.5 }}
          >
            {title}
          </Typography>

          <RadioGroup value={selectedOption} onChange={handleOptionChange}>
            {options.map((option) => (
              <FormControlLabel
                key={option}
                value={option}
                control={<Radio size="small" />}
                label={option}
              />
            ))}
          </RadioGroup>

          {isOtherSelected && (
            <TextField
              value={customValue}
              onChange={(e) => {
                e.stopPropagation();
                setCustomValue(e.target.value);
              }}
              onFocus={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(event) => {
                event.stopPropagation();
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleDone();
                }
              }}
              placeholder={placeholder}
              size="sm"
              fullWidth
              autoComplete="off"
              sx={{ mt: 1 }}
            />
          )}

          <Box
            sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}
          >
            <Button onClick={handleDone} variant="primary">
              Done
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
});

PopoverSelectEditor.displayName = "PopoverSelectEditor";
